<template>
  <div v-if="island.visible" class="followup-island" :style="islandStyle" @mousedown.stop>
    <!-- 紧凑态：选中文本后悬浮「追问」按钮 -->
    <div v-if="island.mode === 'compact'" class="followup-island__compact">
      <button type="button" class="followup-island__trigger" @click="handleExpand">
        <svg
          class="followup-island__trigger-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <polyline points="15 10 20 15 15 20"></polyline>
          <path d="M4 4v7a4 4 0 0 0 4 4h12"></path>
        </svg>
        <span>追问</span>
      </button>
    </div>

    <!-- 展开态：编辑追问内容 -->
    <div v-else class="followup-island__panel">
      <div class="followup-island__title">追问</div>
      <textarea
        ref="textarea"
        v-model="draftText"
        class="followup-island__textarea"
        :placeholder="island.mode === 'edit' ? '编辑追问内容…' : '针对选中内容继续追问…'"
        @input="autoResize"
        @keydown="handleKeyDown"
      ></textarea>
      <div class="followup-island__footer">
        <button
          v-if="island.mode === 'edit'"
          type="button"
          class="followup-island__delete"
          @click="handleDelete"
        >
          删除
        </button>
        <span v-else class="followup-island__footer-spacer"></span>
        <div class="followup-island__actions">
          <button type="button" class="followup-island__btn" @click="handleCancel">取消</button>
          <div class="followup-island__save-group">
            <button type="button" class="followup-island__btn followup-island__btn--primary" @click="handleSave">
              保存
            </button>
            <button
              type="button"
              class="followup-island__btn followup-island__btn--primary followup-island__btn--arrow"
              title="保存并发送"
              @click="handleSaveAndSend"
            >
              <i class="el-icon-arrow-down"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { followUpStore } from '../followUp/store'
import { createTextSelectionAnnotation } from '../followUp/annotation-core'
import { getAnnotationNoteText } from '../followUp/follow-ups'

const MAX_TEXTAREA_HEIGHT = 320

export default {
  name: 'FollowUpIsland',
  data() {
    return {
      draftText: ''
    }
  },
  computed: {
    island() {
      return followUpStore.state.island
    },
    islandStyle() {
      const top = Math.max(36, this.island.anchorY || 0)
      let left = this.island.anchorX || 0
      // 展开态面板宽 330px，避免贴边时超出视口
      if (this.island.mode !== 'compact' && typeof window !== 'undefined') {
        const half = 165
        left = Math.min(Math.max(left, half + 12), window.innerWidth - half - 12)
      }
      return {
        left: `${left}px`,
        top: `${top}px`
      }
    },
    currentAnnotation() {
      if (this.island.mode !== 'edit' || !this.island.annotationId) return null
      return followUpStore.getAnnotation(this.island.messageId, this.island.annotationId)
    }
  },
  watch: {
    'island.visible'(visible) {
      if (visible) {
        this.syncDraft()
      }
    },
    'island.mode'() {
      this.syncDraft()
    },
    'island.annotationId'() {
      this.syncDraft()
    }
  },
  created() {
    this._anchorFrame = null
  },
  mounted() {
    document.addEventListener('mousedown', this.handleDocumentMouseDown, true)
    // 页面/内层滚动容器滚动时让 compact island 跟随选区重新定位
    window.addEventListener('scroll', this.handleViewportChange, true)
    window.addEventListener('resize', this.handleViewportChange)
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.handleDocumentMouseDown, true)
    window.removeEventListener('scroll', this.handleViewportChange, true)
    window.removeEventListener('resize', this.handleViewportChange)
    if (this._anchorFrame) cancelAnimationFrame(this._anchorFrame)
  },
  methods: {
    handleViewportChange() {
      if (!this.island.visible) return
      if (this._anchorFrame) return
      if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
        this.syncAnchor()
        return
      }
      this._anchorFrame = window.requestAnimationFrame(() => {
        this._anchorFrame = null
        this.syncAnchor()
      })
    },
    syncAnchor() {
      if (!this.island.visible) return
      const range = followUpStore.getIslandRange()
      if (!range) return
      // 选区节点已被重新渲染/移除时直接收起
      if (!range.startContainer || !range.startContainer.isConnected) {
        followUpStore.closeIsland()
        return
      }
      const rect = range.getBoundingClientRect()
      if (!rect || (rect.width === 0 && rect.height === 0)) return
      // 选区完全滚出视口后收起
      if (rect.bottom < 0 || rect.top > window.innerHeight) {
        followUpStore.closeIsland()
        return
      }
      followUpStore.updateIslandAnchor({
        anchorX: this.island.anchorX,
        anchorY: Math.max(36, rect.top - 8)
      })
    },
    syncDraft() {
      this.draftText = this.currentAnnotation ? getAnnotationNoteText(this.currentAnnotation) : ''
      this.$nextTick(() => {
        this.autoResize()
        if (this.island.mode !== 'compact' && this.$refs.textarea) {
          this.$refs.textarea.focus()
          const len = this.draftText.length
          this.$refs.textarea.setSelectionRange(len, len)
        }
      })
    },
    autoResize() {
      const ta = this.$refs.textarea
      if (!ta) return
      ta.style.height = 'auto'
      ta.style.height = Math.min(ta.scrollHeight, MAX_TEXTAREA_HEIGHT) + 'px'
    },
    handleExpand() {
      followUpStore.expandIslandToCreate()
    },
    handleDocumentMouseDown(e) {
      if (!this.island.visible) return
      if (this.$el && this.$el.contains(e.target)) return
      followUpStore.closeIsland()
    },
    handleKeyDown(e) {
      if (e.key === 'Escape') {
        e.stopPropagation()
        this.handleCancel()
        return
      }
      if (e.key === 'Enter' && !e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey) {
        e.preventDefault()
        this.handleSave()
      }
    },
    handleCancel() {
      followUpStore.closeIsland()
    },
    handleSave() {
      this.persist()
      followUpStore.closeIsland()
    },
    handleSaveAndSend() {
      const saved = this.persist()
      followUpStore.closeIsland()
      if (saved) {
        followUpStore.requestSend()
      }
    },
    handleDelete() {
      const { messageId, annotationId } = this.island
      followUpStore.removeAnnotation(messageId, annotationId)
      followUpStore.closeIsland()
    },
    persist() {
      const { mode, messageId, annotationId, selection } = this.island
      const note = (this.draftText || '').trim()
      if (mode === 'edit') {
        const annotation = this.currentAnnotation
        if (!annotation) return false
        if (!note && !getAnnotationNoteText(annotation)) {
          // 空注解直接移除
          followUpStore.removeAnnotation(messageId, annotationId)
          return true
        }
        followUpStore.updateAnnotationNote(messageId, annotationId, note)
        return true
      }
      if (mode === 'create' && selection) {
        const annotation = createTextSelectionAnnotation({
          sessionId: this.$store ? this.$store.state.cosmoClaw.currentSessionKey : '',
          messageId,
          startOffset: selection.start,
          endOffset: selection.end,
          quote: selection.quote,
          note
        })
        followUpStore.addAnnotation(messageId, annotation)
        return true
      }
      return false
    }
  }
}
</script>

<style lang="scss" scoped>
.followup-island {
  position: fixed;
  z-index: 400;
  transform: translate(-50%, -100%);
  background: rgba(247, 248, 250, 0.75);
  border: 1px solid rgba(26, 27, 31, 0.02);
  border-radius: 12px;
  box-shadow:
    rgba(26, 27, 31, 0.06) 0 0 0 1px,
    0 1px 2px rgba(0, 0, 0, 0.06),
    0 3px 6px rgba(0, 0, 0, 0.06),
    0 6px 12px rgba(0, 0, 0, 0.04),
    0 12px 24px rgba(0, 0, 0, 0.04);
  backdrop-filter: blur(24px) saturate(1.5);
  animation: followupIslandIn 0.12s ease-out;
}

@keyframes followupIslandIn {
  from {
    opacity: 0;
    transform: translate(-50%, calc(-100% + 4px));
  }
  to {
    opacity: 1;
    transform: translate(-50%, -100%);
  }
}

.followup-island__compact {
  display: flex;
  padding: 4px;
}

.followup-island__trigger {
  height: 30px;
  padding: 0 9px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: rgba(26, 27, 31, 0.85);
  font-size: 13px;
  font-weight: 500;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: rgba(26, 27, 31, 0.05);
    color: #1a1b1f;
  }
}

.followup-island__trigger-icon {
  width: 13px;
  height: 13px;
  flex-shrink: 0;
}

.followup-island__panel {
  width: 330px;
  box-sizing: border-box;
  padding: 11px;
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.followup-island__title {
  padding-left: 4px;
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
  color: #1a1b1f;
}

.followup-island__textarea {
  width: 100%;
  box-sizing: border-box;
  min-height: 44px;
  max-height: 320px;
  padding: 0 0 0 4px;
  border: none;
  outline: none;
  resize: none;
  background: transparent;
  color: #1a1b1f;
  font-size: 13px;
  line-height: 19px;
  font-family: inherit;
  overflow-y: auto;

  &::placeholder {
    color: #9aa3af;
  }
}

.followup-island__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.followup-island__footer-spacer {
  flex: 1;
}

.followup-island__delete {
  border: none;
  background: transparent;
  padding: 0;
  font-size: 13px;
  color: #ef4444;
  cursor: pointer;

  &:hover {
    color: #dc2626;
  }
}

.followup-island__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.followup-island__btn {
  height: 30px;
  padding: 0 11px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: rgba(26, 27, 31, 0.75);
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: rgba(26, 27, 31, 0.05);
  }
}

.followup-island__save-group {
  display: inline-flex;
  align-items: stretch;
  border-radius: 8px;
  background: #fff;
  box-shadow:
    rgba(26, 27, 31, 0.06) 0 0 0 1px,
    0 1px 2px rgba(0, 0, 0, 0.06);
}

.followup-island__btn--primary {
  background: transparent;
  color: #1a1b1f;

  &:hover {
    background: rgba(26, 27, 31, 0.02);
  }
}

.followup-island__btn--arrow {
  border-left: 1px solid rgba(26, 27, 31, 0.04);
  border-radius: 0 8px 8px 0;
  padding: 0 6px;
  color: rgba(26, 27, 31, 0.6);

  i {
    font-size: 12px;
  }
}

.followup-island__save-group .followup-island__btn--primary:first-child {
  border-radius: 8px 0 0 8px;
}
</style>
